import React from 'react';
import { Clock, ChevronDown, ChevronUp, Trash2, X } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { UiSummaryCard, UiSummaryCards } from '../../components/ui/UiSummaryCard';
import UiDateField from '../../components/ui/UiDateField';
import UiTimeField from '../../components/ui/UiTimeField';
import { formatUiDate, formatUiDateTime } from '../../utils/uiFormat';

// Sales report tab — extracted from Reports.js. Presentational: all data and
// handlers arrive via the ctx props-bag assembled by the Reports shell.
export default function SalesReport(ctx) {
  const {
    data,
    formatCurrency,
    periodParts,
    openChartTable,
    getDailyTrendData,
    datePreset,
    openDrillDown,
    normalizedHourlySales,
    COLORS,
    formatRangeDateTick,
    showPreviousSales,
    setShowPreviousSales,
    setShowAddPrevSale,
    setShowSmartPaste,
    previousSalesLoading,
    previousSales,
    previousSalesSummary,
    expandedPrevDay,
    setExpandedPrevDay,
    viewPreviousSaleReceipt,
    deletePreviousSale,
    showAddPrevSale,
    prevSaleDate,
    setPrevSaleDate,
    prevSaleTime,
    setPrevSaleTime,
    prevSalePayment,
    setPrevSalePayment,
    prevSaleItems,
    addPrevSaleItem,
    updatePrevSaleItem,
    removePrevSaleItem,
    submitPreviousSale,
    prevSaleSubmitting,
    menuItems,
    showSmartPaste,
    smartPasteDate,
    setSmartPasteDate,
    smartParsedRows,
    smartPasteText,
    setSmartPasteText,
    parseSmartPaste,
    smartParsing,
    updateSmartRow,
    smartPasteMenuItems,
    removeSmartRow,
    setSmartParsedRows,
    submitSmartPaste,
    smartSubmitting,
  } = ctx;
  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <UiSummaryCards>
        <UiSummaryCard label="Total Sales" value={formatCurrency(data?.summary?.total_sales)} tone="text-primary-600" />
        <UiSummaryCard label="Total Orders / Items" value={<>{data?.summary?.total_orders || 0} <span className="text-gray-400 font-normal">/</span> {data?.summary?.total_items || 0}</>} />
        <UiSummaryCard label="Avg Order / Item" value={<>{formatCurrency(data?.summary?.avg_order_value)} <span className="text-gray-400 font-normal">/</span> {formatCurrency((data?.summary?.total_items > 0 ? (data?.summary?.total_sales || 0) / data.summary.total_items : 0))}</>} />
        <UiSummaryCard label="Period" lines={[periodParts.from, periodParts.to]} />
      </UiSummaryCards>

      {/* Daily Sales Chart */}
      <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base sm:text-lg font-semibold">Daily Sales Trend</h3>
          <button onClick={() => openChartTable(
            'Daily Sales Data',
            getDailyTrendData(),
            datePreset === 'today' ? ['display', 'total', 'count'] : ['date', 'total', 'count'],
            (row) => {
              const p = row.__raw || row;
              if (datePreset === 'today') {
                openDrillDown('hourly_sales', { hour: p.hour }, `Sales at ${p.display}`);
              } else {
                const d = String(p.date || '').substring(0, 10);
                openDrillDown('daily_sales', { date: d }, `Sales on ${formatUiDate(d)}`);
              }
            }
          )} className="text-xs px-3 py-1.5 bg-primary-50 text-primary-700 border border-primary-200 rounded-full hover:bg-primary-100 transition font-medium">View Table</button>
        </div>
        <div className="h-56 sm:h-72 overflow-hidden cursor-pointer">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={getDailyTrendData()} onClick={(e) => {
              if (e && e.activePayload && e.activePayload[0]) {
                const p = e.activePayload[0].payload;
                if (datePreset === 'today') {
                  openDrillDown('hourly_sales', { hour: p.hour }, `Sales at ${p.display}`);
                } else {
                  const d = String(p.date || '').substring(0, 10);
                  openDrillDown('daily_sales', { date: d }, `Sales on ${formatUiDate(d)}`);
                }
              }
            }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey={datePreset === 'today' ? 'display' : 'date'}
                tickFormatter={datePreset !== 'today' ? (v) => formatRangeDateTick(v, datePreset) : undefined}
                tick={{ fontSize: 9 }}
                interval={datePreset === 'today' ? 2 : 0}
                angle={-45}
                textAnchor="end"
                height={45}
              />
              <YAxis />
              <Tooltip
                formatter={(v) => [formatCurrency(v), 'Sales']}
                labelFormatter={(label, items) => datePreset === 'today'
                  ? (items?.[0]?.payload?.display || label)
                  : formatUiDate(String(label).substring(0, 10))}
              />
              <Bar dataKey="total" radius={[4, 4, 0, 0]} cursor="pointer">
                {getDailyTrendData().map((entry, index) => (
                  <Cell key={index} fill={entry.total > 0 ? '#f97316' : '#fed7aa'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Sales by Hour of Day */}
      <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base sm:text-lg font-semibold">Sales by Hour of Day</h3>
          <button onClick={() => openChartTable(
            'Hourly Sales Data',
            normalizedHourlySales,
            ['display', 'total', 'count'],
            (row) => {
              const p = row.__raw || row;
              openDrillDown('hourly_sales', { hour: p.hour }, `Sales at ${p.display}`);
            }
          )} className="text-xs px-3 py-1.5 bg-primary-50 text-primary-700 border border-primary-200 rounded-full hover:bg-primary-100 transition font-medium">View Table</button>
        </div>
        <div className="h-48 sm:h-64 overflow-hidden cursor-pointer">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={normalizedHourlySales} onClick={(e) => {
              if (e && e.activePayload && e.activePayload[0]) {
                const p = e.activePayload[0].payload;
                openDrillDown('hourly_sales', { hour: p.hour }, `Sales at ${p.display}`);
              }
            }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="display" tick={{ fontSize: 9 }} interval={2} angle={-45} textAnchor="end" height={45} />
              <YAxis />
              <Tooltip formatter={(v) => [formatCurrency(v), 'Sales']} />
              <Bar dataKey="total" radius={[4, 4, 0, 0]} cursor="pointer">
                {normalizedHourlySales.map((entry, index) => (
                  <Cell key={index} fill={entry.total > 0 ? '#f97316' : '#fed7aa'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Payment Methods */}
        <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base sm:text-lg font-semibold">By Payment Method</h3>
            <button onClick={() => openChartTable('Payment Method Data', data?.by_payment_method || [], ['payment_method', 'total', 'count'])} className="text-xs px-3 py-1.5 bg-primary-50 text-primary-700 border border-primary-200 rounded-full hover:bg-primary-100 transition font-medium">View Table</button>
          </div>
          <div className="h-48 sm:h-64 overflow-hidden cursor-pointer">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data?.by_payment_method || []} dataKey="total" nameKey="payment_method" cx="50%" cy="50%" outerRadius={70}
                  onClick={(_, index) => {
                    const entry = (data?.by_payment_method || [])[index];
                    if (entry) openDrillDown('payment_method', { payment: entry.payment_method }, `Orders: ${entry.payment_method}`);
                  }}
                  cursor="pointer"
                >
                  {(data?.by_payment_method || []).map((_, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Selling Items */}
        <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base sm:text-lg font-semibold">Top Selling Items</h3>
            <button onClick={() => openChartTable(
              'All Items Sold',
              data?.items_sold || [],
              ['menu_item__name', 'menu_item__category__name', 'qty', 'total'],
              (row) => {
                const p = row.__raw || row;
                openDrillDown('top_selling', { item_name: p.menu_item__name }, `Item: ${p.menu_item__name}`);
              }
            )} className="text-xs px-3 py-1.5 bg-primary-50 text-primary-700 border border-primary-200 rounded-full hover:bg-primary-100 transition font-medium">View All</button>
          </div>
          <div className="space-y-3">
            {(data?.items_sold || []).slice(0, 5).map((item, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition"
                onClick={() => openDrillDown('top_selling', { item_name: item.menu_item__name }, `Item: ${item.menu_item__name}`)}
              >
                <div>
                  <p className="font-medium">{item.menu_item__name}</p>
                  <p className="text-sm text-gray-500">{item.qty} sold</p>
                </div>
                <p className="font-bold text-primary-600">{formatCurrency(item.total)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Department-wise Sales */}
      {data?.by_department?.length > 0 && (
        <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border">
          <h3 className="text-base sm:text-lg font-semibold mb-4">Sales by Department</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.by_department.map((dept, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-100 cursor-pointer hover:bg-purple-100 transition"
                onClick={() => openDrillDown('department_sales', { department: dept.department_name }, `Sales: ${dept.department_name}`)}
              >
                <div>
                  <p className="font-medium text-gray-800">{dept.department_name}</p>
                  <p className="text-xs text-gray-500">{dept.quantity} items sold</p>
                </div>
                <p className="font-bold text-purple-700">{formatCurrency(dept.total)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sales by Category */}
      {(() => {
        const byCat = Object.values(
          (data?.items_sold || []).reduce((acc, item) => {
            const cat = item.menu_item__category__name || 'Uncategorized';
            if (!acc[cat]) acc[cat] = { category_name: cat, total: 0, quantity: 0 };
            acc[cat].total += parseFloat(item.total || 0);
            acc[cat].quantity += parseInt(item.qty || 0);
            return acc;
          }, {})
        ).sort((a, b) => b.total - a.total);
        if (!byCat.length) return null;
        return (
          <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base sm:text-lg font-semibold">Sales by Category</h3>
              <button
                onClick={() => openChartTable(
                  'All Categories',
                  byCat,
                  ['category_name', 'total', 'quantity'],
                  (row) => {
                    const p = row.__raw || row;
                    openDrillDown('category_sales', { category: p.category_name }, `Category: ${p.category_name}`);
                  }
                )}
                className="text-xs px-3 py-1.5 bg-primary-50 text-primary-700 border border-primary-200 rounded-full hover:bg-primary-100 transition font-medium"
              >
                View Table
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {byCat.slice(0, 5).map((cat, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100 cursor-pointer hover:bg-blue-100 transition"
                  onClick={() => openDrillDown('category_sales', { category: cat.category_name }, `Sales: ${cat.category_name}`)}
                >
                  <div>
                    <p className="font-medium text-gray-800">{cat.category_name}</p>
                    <p className="text-xs text-gray-500">{cat.quantity} items sold</p>
                  </div>
                  <p className="font-bold text-blue-700">{formatCurrency(cat.total)}</p>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Previous Sales Section */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <button
          onClick={() => setShowPreviousSales(!showPreviousSales)}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition"
        >
          <h3 className="text-base sm:text-lg font-semibold flex items-center gap-2">
            <Clock size={20} />
            Previous Sales
          </h3>
          {showPreviousSales ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>

        {showPreviousSales && (
          <div className="border-t p-4 space-y-4">
            {previousSalesLoading ? (
              <div className="flex justify-center py-6"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div></div>
            ) : previousSales.length > 0 ? (
              <div className="space-y-5">
                <div className="flex flex-wrap gap-3 text-sm">
                  <span className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700">Orders: <b>{previousSalesSummary.orderCount}</b></span>
                  <span className="px-3 py-1.5 rounded-lg bg-green-50 text-green-700">Total: <b>{formatCurrency(previousSalesSummary.grandTotal)}</b></span>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  {/* Category-wise summary */}
                  <div className="overflow-x-auto">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Category-wise Total</h4>
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Category</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-600">Qty</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-600">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {previousSalesSummary.categories.map((c) => (
                          <tr key={c.category} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-gray-700">{c.category}</td>
                            <td className="px-3 py-2 text-right text-gray-600">{c.quantity}</td>
                            <td className="px-3 py-2 text-right font-semibold">{formatCurrency(c.total)}</td>
                          </tr>
                        ))}
                        {previousSalesSummary.categories.length === 0 && (
                          <tr><td colSpan={3} className="px-3 py-3 text-center text-gray-400 text-xs">No item detail available</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Day-wise summary */}
                  <div className="overflow-x-auto">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Day-wise Total</h4>
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Date</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-600">Orders</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-600">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {previousSalesSummary.days.map((d) => {
                          const isOpen = expandedPrevDay === d.date;
                          const dayOrders = isOpen ? previousSales.filter(o => String(o.created_at || '').slice(0, 10) === d.date) : [];
                          return (
                            <React.Fragment key={d.date}>
                              <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setExpandedPrevDay(isOpen ? null : d.date)}>
                                <td className="px-3 py-2 text-gray-700">{isOpen ? '▾' : '▸'} {formatUiDate(d.date)}</td>
                                <td className="px-3 py-2 text-right text-gray-600">{d.orders}</td>
                                <td className="px-3 py-2 text-right font-semibold">{formatCurrency(d.total)}</td>
                              </tr>
                              {isOpen && dayOrders.map((order) => (
                                <tr key={order.id} className="bg-gray-50/60 text-xs">
                                  <td className="pl-6 pr-3 py-1.5 text-primary-600 cursor-pointer hover:underline" onClick={() => viewPreviousSaleReceipt(order)}>
                                    {order.order_number} · {(order.items || []).map(it => it.menu_item_name || it.menu_item?.name || '?').join(', ')}
                                  </td>
                                  <td className="px-3 py-1.5 text-right text-gray-500">{formatUiDateTime(order.created_at).split(' ').slice(-2).join(' ')}</td>
                                  <td className="px-3 py-1.5 text-right">
                                    <span className="font-semibold mr-2">{formatCurrency(order.total_amount)}</span>
                                    <button onClick={() => deletePreviousSale(order.id)} className="text-red-500 hover:text-red-700 align-middle" title="Delete this previous sale"><Trash2 size={14} /></button>
                                  </td>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">No previous sales found in this date range</p>
            )}
          </div>
        )}
      </div>

      {/* Add Previous Sale Modal */}
      {showAddPrevSale && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowAddPrevSale(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Add Previous Sale</h3>
              <button onClick={() => setShowAddPrevSale(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Date</label>
                  <UiDateField value={prevSaleDate} onChange={setPrevSaleDate} />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Time</label>
                  <UiTimeField value={prevSaleTime} onChange={setPrevSaleTime} />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Payment Method</label>
                <select value={prevSalePayment} onChange={e => setPrevSalePayment(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="digital_wallet">Digital</option>
                </select>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-gray-600">Items</label>
                  <button onClick={addPrevSaleItem} className="text-xs text-primary-600 hover:text-primary-700 font-medium">+ Add Item</button>
                </div>
                {prevSaleItems.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <select
                      value={item.menu_item_id}
                      onChange={e => updatePrevSaleItem(idx, 'menu_item_id', e.target.value)}
                      className="flex-1 border border-gray-200 rounded-lg px-2 py-2 text-sm"
                    >
                      <option value="">Select item...</option>
                      {menuItems.map(mi => (
                        <option key={mi.id} value={mi.id}>{mi.name} ({formatCurrency(mi.selling_price)})</option>
                      ))}
                    </select>
                    <input
                      type="number" min="1" value={item.quantity}
                      onChange={e => updatePrevSaleItem(idx, 'quantity', e.target.value)}
                      className="w-16 border border-gray-200 rounded-lg px-2 py-2 text-sm text-center"
                      placeholder="Qty"
                    />
                    <input
                      type="number" step="0.01" value={item.unit_price}
                      onChange={e => updatePrevSaleItem(idx, 'unit_price', e.target.value)}
                      className="w-24 border border-gray-200 rounded-lg px-2 py-2 text-sm"
                      placeholder="Price"
                    />
                    <button onClick={() => removePrevSaleItem(idx)} className="p-1 text-red-400 hover:text-red-600"><X size={16} /></button>
                  </div>
                ))}
              </div>
              <button
                onClick={submitPreviousSale}
                disabled={prevSaleSubmitting}
                className="w-full py-2.5 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition disabled:opacity-50"
              >{prevSaleSubmitting ? 'Saving...' : 'Save Previous Sale'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Smart Paste Modal */}
      {showSmartPaste && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowSmartPaste(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Smart Paste Sales</h3>
              <button onClick={() => setShowSmartPaste(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Sale Date</label>
                <UiDateField value={smartPasteDate} onChange={setSmartPasteDate} />
              </div>

              {smartParsedRows.length === 0 ? (
                <>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Paste sales text</label>
                    <textarea
                      value={smartPasteText}
                      onChange={e => setSmartPasteText(e.target.value)}
                      rows={8}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono"
                      placeholder={"Mango Shake  2  350  14:30\nStrawberry Juice  250  14:30\n550  15:00"}
                    />
                    <p className="text-[11px] text-gray-400 mt-1">Format: menu item, count (optional), total price, time (optional). One entry per line.</p>
                  </div>
                  <button
                    onClick={parseSmartPaste}
                    disabled={smartParsing}
                    className="w-full py-2.5 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition disabled:opacity-50"
                  >{smartParsing ? 'Parsing...' : 'Parse & Review'}</button>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-600">{smartParsedRows.length} row(s) parsed. Review and correct below:</p>
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-2 py-2 text-left text-xs font-medium text-gray-600">Menu Item</th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 w-16">Qty</th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 w-24">Price</th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 w-24">Time</th>
                          <th className="px-2 py-2 w-8"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {smartParsedRows.map((row, idx) => (
                          <tr key={idx} className={row.matched_id ? '' : 'bg-red-50'}>
                            <td className="px-2 py-2">
                              <select
                                value={row.matched_id || ''}
                                onChange={e => updateSmartRow(idx, 'matched_id', e.target.value)}
                                className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm"
                              >
                                <option value="">{row.item_name || 'Select item...'}</option>
                                {(smartPasteMenuItems || []).map(mi => (
                                  <option key={mi.id} value={mi.id}>{mi.name} ({formatCurrency(mi.selling_price)})</option>
                                ))}
                              </select>
                              {row.modifier_text && <p className="text-[11px] text-purple-600 mt-0.5">Modifier: {row.modifier_text}</p>}
                            </td>
                            <td className="px-2 py-2">
                              <input
                                type="number" min="1" value={row.quantity}
                                onChange={e => updateSmartRow(idx, 'quantity', parseInt(e.target.value) || 1)}
                                className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm text-center"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <input
                                type="number" step="0.01" value={row.total_price || ''}
                                onChange={e => updateSmartRow(idx, 'total_price', parseFloat(e.target.value) || null)}
                                className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm text-center"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <UiTimeField
                                value={row.time || ''}
                                onChange={v => updateSmartRow(idx, 'time', v)}
                                inputClassName="!py-1"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <button onClick={() => removeSmartRow(idx)} className="p-1 text-red-400 hover:text-red-600"><X size={14} /></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setSmartParsedRows([]); }}
                      className="flex-1 py-2.5 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition"
                    >Back to Paste</button>
                    <button
                      onClick={submitSmartPaste}
                      disabled={smartSubmitting || !smartParsedRows.some(r => r.matched_id)}
                      className="flex-1 py-2.5 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition disabled:opacity-50"
                    >{smartSubmitting ? 'Submitting...' : `Submit ${smartParsedRows.filter(r => r.matched_id).length} Sale(s)`}</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
